/**
 * Dependency-free app-variant manifest — the SINGLE SOURCE OF TRUTH for variant
 * identity. It is shared by two very different runtimes:
 *
 *   1. The React Native app at runtime (`app/config/appVariant.js`), bundled by
 *      Metro/Babel as an ES module.
 *   2. The Expo build config (`app.config.js`), evaluated by Node in the
 *      `@expo/config` loader.
 *
 * Because (2) runs in plain Node BEFORE any native module exists, this file MUST
 * NOT import `react-native`, `react-native-device-info`, or any native binding.
 * Keep it pure data + CommonJS (`module.exports`) so both consumers can load it.
 *
 * `packageId` doubles as the iOS bundle identifier AND the Android applicationId
 * for each variant (they are intentionally kept identical per variant).
 *
 * NOTE on the `sales` token: it means TWO different things and they must not be
 * confused. The variant `pos` ("Happy POS") is the POS-only build — it keeps the
 * historical `org.happysolutions.sales` applicationId because that build has an
 * installed base and changing an applicationId breaks upgrades. The variant
 * `sales` ("Happy Sales") is the NEW CRM-driven field-sales app, which ships the
 * `sales` module and the `sales` app mode.
 *
 * @typedef {'global'|'pos'|'sales'|'management'|'service'|'warehouse'} AppVariantId
 * @typedef {'restaurant'|'sales'|'manager'|'service'|'stock'|'driver'} AppMode
 */

/** @type {Record<AppVariantId, {id: AppVariantId, name: string, packageId: string, legacyPackageIds?: string[], allowedModes: AppMode[], defaultMode: AppMode|null, modules: {pos: boolean, sales: boolean, management: boolean, service: boolean, warehouse: boolean, driver: boolean}}>} */
const APP_VARIANTS = {
    global: {
        id: 'global',
        name: 'Happy App',
        packageId: 'org.happysolutions.app',
        // iOS ships the global app under the `hu.` bundle id (Apple App ID); Android
        // keeps the `org.` applicationId. Both must resolve to the global variant.
        iosPackageId: 'hu.happysolutions.app',
        legacyPackageIds: ['hu.happysolutions.app'],
        allowedModes: ['restaurant', 'sales', 'manager', 'service', 'stock', 'driver'],
        defaultMode: null,
        modules: {
            pos: true,
            sales: true,
            management: true,
            service: true,
            warehouse: true,
            driver: true,
        },
    },
    pos: {
        id: 'pos',
        name: 'Happy POS',
        // DO NOT change this applicationId/bundle id: the POS build shipped for
        // years under `…sales` (it used to be called "Happy Sales") and has an
        // installed base — a new applicationId would break every upgrade. Only
        // the variant id and the display name were renamed to `pos`/"Happy POS".
        packageId: 'org.happysolutions.sales',
        allowedModes: ['restaurant'],
        defaultMode: 'restaurant',
        modules: {
            pos: true,
            sales: false,
            management: false,
            service: false,
            warehouse: false,
            driver: false,
        },
    },
    sales: {
        id: 'sales',
        name: 'Happy Sales',
        // The NEW CRM-driven field-sales app — a different product from the POS
        // build above, hence its own `crm` applicationId.
        packageId: 'org.happysolutions.crm',
        allowedModes: ['sales'],
        defaultMode: 'sales',
        modules: {
            pos: false,
            sales: true,
            management: false,
            service: false,
            warehouse: false,
            driver: false,
        },
    },
    management: {
        id: 'management',
        name: 'Happy Management',
        packageId: 'org.happysolutions.management',
        allowedModes: ['manager'],
        defaultMode: 'manager',
        modules: {
            pos: false,
            sales: false,
            management: true,
            service: false,
            warehouse: false,
            driver: false,
        },
    },
    service: {
        id: 'service',
        name: 'Happy Service',
        // Must match the Android `service` product flavor applicationId (Happy Garage).
        packageId: 'hu.happysolutions.service',
        // Older builds shipped under this id; keep it mapped so they resolve correctly.
        legacyPackageIds: ['hu.happysolutions.work'],
        allowedModes: ['service'],
        defaultMode: 'service',
        modules: {
            pos: false,
            sales: false,
            management: false,
            service: true,
            warehouse: false,
            driver: false,
        },
    },
    warehouse: {
        id: 'warehouse',
        name: 'Happy Warehouse',
        // Must match the Android `warehouse` product flavor applicationId (Happy Logistic).
        packageId: 'org.happysolutions.logistic',
        legacyPackageIds: ['org.happysolutions.warehouse'],
        allowedModes: ['stock'],
        defaultMode: 'stock',
        modules: {
            pos: false,
            sales: false,
            management: false,
            service: false,
            warehouse: true,
            driver: false,
        },
    },
};

/**
 * Home screen route for each operational app mode. These routes are only
 * registered in the navigator when their hosting module is bundled.
 * @type {Record<AppMode, string>}
 */
const MODULE_HOME_ROUTES = {
    restaurant: 'HomeScreen',
    // The Sales (CRM field-sales) screens are still being built — the route is
    // declared here so every mode has a home, but the navigator only registers
    // it once the screen exists (see AppNavigator: unknown routes are skipped).
    sales: 'SalesHomeScreen',
    manager: 'ManagementHomeScreen',
    service: 'RepairmanHomeScreen',
    stock: 'WarehousemanHomeScreen',
    driver: 'DriverHomeScreen',
};

/**
 * Desktop MINI APPS — single-purpose installable apps (macOS Dock / Windows Start
 * menu) cut from the same web bundle as the full Happy App. They are deliberately
 * NOT entries of `APP_VARIANTS`: a variant is a store-distributed mobile product
 * with an Android flavor and a mode/module set (and the manifest tests enforce
 * that 1:1), whereas a mini app is a desktop-only shell that pins the user to ONE
 * universal screen (Time Tracker / Team Chat) and bundles NO operational module.
 *
 * Selected at BUILD time by `EXPO_PUBLIC_HAPPY_APP_VARIANT=<id>` (or the explicit
 * `EXPO_PUBLIC_HAPPY_MINI_APP`), set by the `*:tauri:<id>` npm scripts next to the matching
 * `src-tauri/tauri.<id>.conf.json` overlay that gives the installer its own
 * name, identifier and icon). `identifier` MUST equal that overlay's
 * `identifier` — the Rust side (`src-tauri/src/miniapp.rs`) keys its tray / badge /
 * autostart behaviour off it.
 *
 * @typedef {'timetracker'|'teamchat'} MiniAppId
 * @type {Record<MiniAppId, {id: MiniAppId, name: string, identifier: string, homeRoute: string, accent: string}>}
 */
const MINI_APPS = {
    timetracker: {
        id: 'timetracker',
        name: 'Happy Time',
        identifier: 'hu.happysolutions.timetracker',
        homeRoute: 'TimeTrackerScreen',
        accent: '#F97316',
    },
    teamchat: {
        id: 'teamchat',
        name: 'Happy Chat',
        identifier: 'hu.happysolutions.teamchat',
        homeRoute: 'ChatHomeScreen',
        accent: '#F97316',
    },
};

/**
 * Resolve a mini-app id from the build env; `null` for every normal build.
 * @param {Record<string, string|undefined>} [env=process.env]
 * @returns {MiniAppId|null}
 */
const resolveMiniAppIdFromEnv = (env = (typeof process !== 'undefined' ? process.env : {})) => {
    // `EXPO_PUBLIC_HAPPY_APP_VARIANT=timetracker|teamchat` is what the
    // `*:tauri:<id>` npm scripts set; it is honoured here (not in APP_VARIANTS)
    // because a mini app is not a store variant. `…_MINI_APP` is the explicit alias.
    const requested = env?.EXPO_PUBLIC_HAPPY_MINI_APP || env?.HAPPY_MINI_APP
        || env?.EXPO_PUBLIC_HAPPY_APP_VARIANT || env?.HAPPY_APP_VARIANT;
    return requested && MINI_APPS[requested] ? requested : null;
};

/**
 * Resolve a variant id from an explicit env override, falling back to 'global'.
 * Shared by the build config and (indirectly) the runtime so both pick the same
 * variant from the same `EXPO_PUBLIC_HAPPY_APP_VARIANT` / `HAPPY_APP_VARIANT`.
 * @param {Record<string, string|undefined>} [env=process.env]
 * @returns {AppVariantId}
 */
const resolveVariantIdFromEnv = (env = (typeof process !== 'undefined' ? process.env : {})) => {
    const requested = env?.EXPO_PUBLIC_HAPPY_APP_VARIANT || env?.HAPPY_APP_VARIANT;
    return requested && APP_VARIANTS[requested] ? requested : 'global';
};

module.exports = {
    APP_VARIANTS,
    MINI_APPS,
    MODULE_HOME_ROUTES,
    resolveMiniAppIdFromEnv,
    resolveVariantIdFromEnv,
};
